import React, { useEffect, useState, createContext, useContext } from 'react';
import { Instrument, CaseDetails, ScanMetrics } from '../types/instruments';
interface CaseState {
  instruments: Instrument[];
  caseDetails: CaseDetails;
  metrics: ScanMetrics;
  lastScanTime: string;
  hasReviewedChecklist: boolean;
  scanAttempts: number;
}
interface CaseContextType extends CaseState {
  setInstruments: (instruments: Instrument[]) => void;
  setMetrics: (metrics: ScanMetrics) => void;
  setLastScanTime: (time: string) => void;
  setHasReviewedChecklist: (reviewed: boolean) => void;
  incrementScanAttempts: () => void;
  resetCase: () => void;
  hasUnsavedChanges: boolean;
}
const CaseContext = createContext<CaseContextType | undefined>(undefined);
const INITIAL_INSTRUMENTS: Instrument[] = [{
  id: '1',
  name: 'Scalpel Handle #3',
  category: 'Cutting',
  slot: 'A1',
  status: 'pending',
  expected: 1,
  found: 0
}, {
  id: '2',
  name: 'Mayo Scissors',
  category: 'Cutting',
  slot: 'A2',
  status: 'pending',
  expected: 1,
  found: 0
}, {
  id: '3',
  name: 'Adson Forceps',
  category: 'Grasping',
  slot: 'B1',
  status: 'pending',
  expected: 2,
  found: 0
}, {
  id: '4',
  name: 'Kelly Clamp',
  category: 'Clamping',
  slot: 'B2',
  status: 'pending',
  expected: 2,
  found: 0
}, {
  id: '5',
  name: 'Needle Holder',
  category: 'Suturing',
  slot: 'C1',
  status: 'pending',
  expected: 1,
  found: 0
}, {
  id: '6',
  name: 'Army-Navy Retractor',
  category: 'Retracting',
  slot: 'C2',
  status: 'pending',
  expected: 2,
  found: 0
}, {
  id: '7',
  name: 'Sponge Forceps',
  category: 'Grasping',
  slot: 'D1',
  status: 'pending',
  expected: 2,
  found: 0
}, {
  id: '8',
  name: 'Towel Clip',
  category: 'Clamping',
  slot: 'D2',
  status: 'pending',
  expected: 2,
  found: 0
}];
const INITIAL_CASE_DETAILS: CaseDetails = {
  id: '#SRG-9902',
  room: 'OR Room 4',
  startTime: new Date(Date.now() - 1000 * 60 * 45)
};
export function CaseProvider({
  children
}: {
  children: ReactNode;
}) {
  const [instruments, setInstruments] = useState<Instrument[]>(() => {
    const saved = sessionStorage.getItem('caseState');
    return saved ? JSON.parse(saved).instruments : INITIAL_INSTRUMENTS;
  });
  const [caseDetails] = useState<CaseDetails>(INITIAL_CASE_DETAILS);
  const [metrics, setMetrics] = useState<ScanMetrics>({
    certainty: 0,
    detectedCount: 0,
    latency: 0
  });
  const [lastScanTime, setLastScanTime] = useState('');
  const [hasReviewedChecklist, setHasReviewedChecklist] = useState(false);
  const [scanAttempts, setScanAttempts] = useState(0);
  // Detect unsaved changes
  const hasUnsavedChanges = instruments.some(i => i.status !== 'pending') && !instruments.every(i => i.status === 'ok' || i.status === 'override');
  // Persist state to sessionStorage
  useEffect(() => {
    const state = {
      instruments,
      metrics,
      lastScanTime,
      scanAttempts
    };
    sessionStorage.setItem('caseState', JSON.stringify(state));
  }, [instruments, metrics, lastScanTime, scanAttempts]);
  const incrementScanAttempts = () => setScanAttempts(prev => prev + 1);
  const resetCase = () => {
    setInstruments(INITIAL_INSTRUMENTS);
    setMetrics({
      certainty: 0,
      detectedCount: 0,
      latency: 0
    });
    setLastScanTime('');
    setHasReviewedChecklist(false);
    setScanAttempts(0);
    sessionStorage.removeItem('caseState');
  };
  return <CaseContext.Provider value={{
    instruments,
    caseDetails,
    metrics,
    lastScanTime,
    hasReviewedChecklist,
    scanAttempts,
    setInstruments,
    setMetrics,
    setLastScanTime,
    setHasReviewedChecklist,
    incrementScanAttempts,
    resetCase,
    hasUnsavedChanges
  }} data-id="element-380">
      {children}
    </CaseContext.Provider>;
}
export function useCaseContext() {
  const context = useContext(CaseContext);
  if (!context) {
    throw new Error('useCaseContext must be used within CaseProvider');
  }
  return context;
}